import { useCallback, useMemo, useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Info, Shield, CheckCircle2 } from 'lucide-react';
import { SessionDefaultsInheritanceChainRows } from './SessionDefaultsInheritanceChain';
import type { DegapProcess } from '@/types/prioritization/degapCycle';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useReadinessDefinitionStore } from '@/stores/prioritization/readinessDefinitionStore';
import { resolveEffectiveReadinessDefinitionId } from '@/lib/readinessDefinitionResolver';
import {
  workspaceLayerFromWorkspace,
  cycleLayerFromCycle,
  getSessionDefaultsInheritanceBreakdown,
  type SessionTimingMode,
  type SessionEntryMode,
  type SessionDefaultsInMetadata,
} from '@/lib/effectiveConfigResolver';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { useSessionPolicyStore, type UpsertSessionPolicyInput } from '@/stores/prioritization/sessionPolicyStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { RankOrderChangeSetting } from './RankOrderChangeSetting';
import { RoomRulesSettings } from './RoomRulesSettings';
import { fetchSessionDefaults, withServerAbove, type SessionDefaults } from '@/lib/sessionDefaults';
import { Select } from '@/components/ds';

export interface CycleSessionDefaultsSectionProps {
  cycle: DegapProcess | null;
}

function getCycleSessionDefaults(cycle: DegapProcess | null): SessionDefaultsInMetadata {
  if (!cycle?.metadata || typeof cycle.metadata !== 'object') return {};
  const d = cycle.metadata.sessionDefaults;
  return (d && typeof d === 'object') ? (d as SessionDefaultsInMetadata) : {};
}

export function CycleSessionDefaultsSection({ cycle }: CycleSessionDefaultsSectionProps) {
  const updateCycle = useDegapProcessStore((s) => s.updateCycle);
  const applyPolicyToCycleLocal = useDegapProcessStore((s) => s.applyPolicyToCycleLocal);
  const upsertPolicy = useSessionPolicyStore((s) => s.upsertPolicy);
  const [busy, setBusy] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(true);
  const { users } = useSystemUsers();
  const getCurrentWorkspace = useTenantStore((s) => s.getCurrentWorkspace);
  const can = useUserStore((s) => s.can);
  const userId = useUserStore((s) => s.userId);
  // The same two paths set_session_policy allows (migration 1012).
  const canConfigureCycle = can('process.edit') || (Boolean(userId) && userId === cycle?.ownerId);

  // ── Readiness definition inheritance ────────────────────────────────────────
  const rdStore = useReadinessDefinitionStore();
  useEffect(() => {
    const ws = getCurrentWorkspace();
    if (ws?.id && rdStore.definitions.length === 0 && !rdStore.loading) {
      rdStore.loadForWorkspace(ws.id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const workspaceReadinessId = rdStore.workspaceDefinitionId;
  const effectiveReadinessId = useMemo(
    () =>
      resolveEffectiveReadinessDefinitionId(
        workspaceReadinessId,
        cycle?.readinessDefinitionId ?? null,
        null,
        null,
      ),
    [workspaceReadinessId, cycle],
  );

  const effectiveReadinessDef = rdStore.definitions.find((d) => d.id === effectiveReadinessId?.id) ?? null;
  const cycleReadinessValue = cycle?.readinessDefinitionId ?? '';

  const handleReadinessChange = useCallback(
    async (value: string) => {
      if (!cycle?.id) return;
      setBusy('readiness');
      try {
        await updateCycle(cycle.id, { readinessDefinitionId: value === '' ? null : value } as Parameters<typeof updateCycle>[1]);
      } finally {
        setBusy(null);
      }
    },
    [cycle?.id, updateCycle]
  );

  const defs = getCycleSessionDefaults(cycle);
  const timingValue = (defs.timingMode === 'live' || defs.timingMode === 'async' ? defs.timingMode : '') as string;
  const entryValue = (defs.entryMode === 'facilitator' || defs.entryMode === 'participants' ? defs.entryMode : '') as string;
  const facilitatorValue = defs.defaultFacilitatorId ?? '';
  const resultResolutionValue = (defs.resultResolution === 'latest_published' || defs.resultResolution === 'aggregate' || defs.resultResolution === 'selected_official' ? defs.resultResolution : '') as string;

  // What sits above this cycle is the server's answer (S-40, get_session_defaults
  // from 'workflow'): the cycle's workflow, else the workspace's default, then the
  // workspace. The browser breakdown alone skipped the workflow.
  const [above, setAbove] = useState<SessionDefaults | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!cycle?.id) return;
    void fetchSessionDefaults({ cycleId: cycle.id }, 'workflow').then(({ defaults }) => {
      if (!cancelled) setAbove(defaults);
    });
    return () => { cancelled = true; };
  }, [cycle?.id, cycle?.workflowId]);

  const breakdown = useMemo(() => {
    const workspace = getCurrentWorkspace();
    const workspaceLayer = workspaceLayerFromWorkspace(workspace ?? null);
    const cycleLayer = cycleLayerFromCycle(cycle);
    const facilitatorLabel = (id: string) => users.find((u) => u.id === id)?.name || users.find((u) => u.id === id)?.email || id;
    return withServerAbove(
      getSessionDefaultsInheritanceBreakdown(workspaceLayer ?? null, cycleLayer ?? null, null, null, { facilitatorLabel }),
      above,
      facilitatorLabel,
    );
  }, [getCurrentWorkspace, cycle, users, above]);

  const savePolicy = useCallback(
    async (changes: Omit<UpsertSessionPolicyInput, 'scopeKind' | 'scopeId'>) => {
      if (!cycle?.id) return;
      setSaveError(null);
      const saved = await upsertPolicy({ scopeKind: 'cycle', scopeId: cycle.id, ...changes });
      if (saved) applyPolicyToCycleLocal(cycle.id);
      else setSaveError(useSessionPolicyStore.getState().error ?? 'The setting could not be saved.');
    },
    [cycle?.id, upsertPolicy, applyPolicyToCycleLocal]
  );

  const handleChange = useCallback(
    async (field: string, value: string) => {
      if (!cycle?.id) return;
      setBusy(field);
      try {
        const currentDefs = getCycleSessionDefaults(cycle);
        let nextDefs: SessionDefaultsInMetadata;
        // Phase C-2 (canon 28 §6, migration 598): the three policy keys live on
        // session_policies ('cycle' scope), never in the cycle's metadata.
        if (field === 'timing') {
          await savePolicy({ timingMode: (value === '' ? null : value) as SessionTimingMode | null });
          return;
        }
        if (field === 'entry') {
          await savePolicy({ entryMode: (value === '' ? null : value) as SessionEntryMode | null });
          return;
        }
        if (field === 'facilitator') {
          await savePolicy({ defaultFacilitatorId: value === '' ? null : value });
          return;
        }
        if (field === 'resultResolution') {
          nextDefs = { ...currentDefs, resultResolution: value === '' ? undefined : value };
        } else {
          nextDefs = currentDefs;
        }
        await updateCycle(cycle.id, {
          metadata: { ...cycle.metadata, sessionDefaults: nextDefs },
        });
      } finally {
        setBusy(null);
      }
    },
    [cycle, updateCycle, savePolicy]
  );

  const allowTrackOverride: boolean =
    typeof cycle?.metadata?.allowTrackOverride === 'boolean'
      ? (cycle.metadata.allowTrackOverride as boolean)
      : true;

  const handleAllowTrackOverrideChange = useCallback(
    async (value: boolean) => {
      if (!cycle?.id) return;
      setBusy('allowTrackOverride');
      try {
        await updateCycle(cycle.id, {
          metadata: { ...cycle.metadata, allowTrackOverride: value },
        });
      } finally {
        setBusy(null);
      }
    },
    [cycle, updateCycle]
  );

  if (!cycle) return null;

  const settings = [
    {
      key: 'timing',
      label: 'Session mode',
      value: timingValue,
      effective: breakdown.timingMode,
      options: [
        { value: '', label: 'Inherit' },
        { value: 'live', label: 'Live' },
        { value: 'async', label: 'Async' },
      ],
    },
    {
      key: 'entry',
      label: 'Data entry',
      value: entryValue,
      effective: breakdown.entryMode,
      options: [
        { value: '', label: 'Inherit' },
        { value: 'facilitator', label: 'Facilitator' },
        { value: 'participants', label: 'Participants' },
      ],
    },
    {
      key: 'facilitator',
      label: 'Facilitator',
      value: facilitatorValue,
      effective: breakdown.defaultFacilitatorId,
      options: [
        { value: '', label: 'Inherit' },
        ...users.map((u) => ({ value: u.id, label: u.name || u.email || u.id })),
      ],
    },
    {
      key: 'resultResolution',
      label: 'Which result counts',
      value: resultResolutionValue,
      effective: {
        global: 'From workflow',
        cycle: resultResolutionValue ? resultResolutionValue.replace(/_/g, ' ') : '—',
        track: '—',
        session: '—',
        effective: resultResolutionValue ? resultResolutionValue.replace(/_/g, ' ') : 'From workflow',
        source: (resultResolutionValue ? 'cycle' : 'global') as 'global' | 'cycle' | 'track' | 'session',
      },
      options: [
        { value: '', label: 'Inherit' },
        { value: 'latest_published', label: 'Latest published' },
        { value: 'selected_official', label: 'Chosen session' },
        // S-22: combining results is not built yet — shown, never chosen.
        { value: 'aggregate', label: 'Combined (not available yet)', disabled: true },
      ],
    },
    // S-43: a cycle's default duration and its own Catchball switch are gone. Nothing
    // read them; whether Catchball runs is the workflow's setting.
  ];

  return (
    <div className="rounded-lg border border-gray-200 dark:border-[var(--rule)] bg-white dark:bg-[var(--surface-raised)] overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100 dark:border-[var(--rule)]">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-[var(--text-pri)]">Session defaults</h3>
        <span className="text-xs text-gray-500 dark:text-[var(--text-sec)]">Inherited unless overridden</span>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-[var(--rule)]">
        {settings.map((s) => {
          const isOverridden = s.value !== '';
          return (
            <div key={s.key} className="px-4 py-2.5 flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700 dark:text-[var(--text-sec)] w-28 shrink-0">{s.label}</span>

              {!canConfigureCycle ? (
                isOverridden && (
                  <span className="text-sm text-gray-900 dark:text-[var(--text-pri)]">
                    {s.options.find((o) => o.value === s.value)?.label ?? s.value}
                  </span>
                )
              ) : (
              <Select
                value={s.value}
                onChange={(e) => handleChange(s.key, e.target.value)}
                disabled={busy !== null}
                className={`text-sm rounded border px-2.5 py-1.5 min-w-[140px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-[var(--surface)] dark:text-[var(--text-pri)] ${
                  isOverridden
                    ? 'border-blue-300 bg-blue-50/50 text-gray-900 dark:border-blue-500/50 dark:bg-blue-900/20'
                    : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-[var(--rule)] dark:bg-[var(--surface)]'
                }`}
              >
                {s.options.map((o) => (
                  <option key={o.value} value={o.value} disabled={'disabled' in o ? Boolean(o.disabled) : undefined}>{o.label}</option>
                ))}
              </Select>
              )}

              {!isOverridden && (
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="text-sm font-medium text-gray-700 dark:text-[var(--text-pri)] truncate">{s.effective.effective}</span>
                  <span className="text-xs text-gray-400 dark:text-[var(--text-sec)] shrink-0">via {s.effective.source === 'global' ? (s.effective.globalLabel ?? 'workspace') : s.effective.source}</span>
                </span>
              )}
            </div>
          );
        })}
        {/* S-33: may the ranking order and cut line change after the run? */}
        {cycle?.id && (
          <>
          <RankOrderChangeSetting
            scopeKind="cycle"
            scopeId={cycle.id}
            ids={{ workspaceId: cycle.workspaceId ?? getCurrentWorkspace()?.id ?? null, workflowId: cycle.workflowId ?? above?.workflow?.id ?? null, cycleId: cycle.id }}
            canEdit={canConfigureCycle}
          />
          <RoomRulesSettings
            scopeKind="cycle"
            scopeId={cycle.id}
            ids={{ workspaceId: cycle.workspaceId ?? getCurrentWorkspace()?.id ?? null, workflowId: cycle.workflowId ?? above?.workflow?.id ?? null, cycleId: cycle.id }}
            canEdit={canConfigureCycle}
          />
          </>
        )}
        {saveError && <p role="alert" className="px-4 py-2 text-xs text-[var(--coral)]">{saveError}</p>}
      </div>

      <div className="border-t border-gray-100 dark:border-[var(--rule)]">
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="w-full px-4 py-2 flex items-center gap-1.5 text-xs text-gray-500 dark:text-[var(--text-sec)] hover:text-gray-700 dark:hover:text-[var(--text-pri)] hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)] transition-colors"
        >
          <Info className="h-3 w-3" />
          Inheritance chain
          {showDetails ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
        </button>
        {showDetails && (
          <div className="px-4 pb-4 pt-0.5">
            <SessionDefaultsInheritanceChainRows
              depth="cycle"
              rows={settings.map((s) => ({
                key: s.key,
                label: s.label,
                layer: s.effective,
              }))}
            />
          </div>
        )}
      </div>

      {/* Readiness definition */}
      <div className="border-t border-gray-200 dark:border-[var(--rule)] px-4 py-3">
        <div className="flex items-center gap-2 mb-2.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
          <span className="text-xs font-semibold text-gray-600 dark:text-[var(--text-sec)] uppercase tracking-wide">Readiness definition</span>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={cycleReadinessValue}
            onChange={(e) => handleReadinessChange(e.target.value)}
            disabled={busy === 'readiness'}
            className={`text-sm rounded border px-2.5 py-1.5 min-w-[200px] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-[var(--surface)] dark:text-[var(--text-pri)] ${
              cycleReadinessValue
                ? 'border-blue-300 bg-blue-50/50 text-gray-900 dark:border-blue-500/50 dark:bg-blue-900/20'
                : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-[var(--rule)] dark:bg-[var(--surface)]'
            }`}
          >
            <option value="">Inherit from workspace</option>
            {rdStore.definitions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </Select>
          {!cycleReadinessValue && (
            <span className="text-sm text-gray-500 dark:text-[var(--text-sec)]">
              {effectiveReadinessDef ? (
                <span className="font-medium text-gray-700 dark:text-[var(--text-pri)]">{effectiveReadinessDef.name}</span>
              ) : (
                <span className="italic">None defined</span>
              )}
              {effectiveReadinessId && <span className="ml-1 text-xs text-gray-400 dark:text-[var(--text-sec)]">via {effectiveReadinessId.source}</span>}
            </span>
          )}
        </div>
        <p className="mt-1.5 text-xs text-gray-400 dark:text-[var(--text-sec)]">
          Sets the default readiness definition for all tracks in this cycle (tracks can override).
        </p>
      </div>

      {/* Override permissions */}
      <div className="border-t border-gray-200 dark:border-[var(--rule)] px-4 py-3 bg-gray-50/50 dark:bg-[var(--surface)]">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-3.5 w-3.5 text-gray-400 dark:text-[var(--text-sec)]" />
          <span className="text-xs font-semibold text-gray-600 dark:text-[var(--text-sec)] uppercase tracking-wide">Override permissions</span>
        </div>
        <label className="flex items-start justify-between gap-4 cursor-pointer group">
          <div>
            <p className="text-sm text-gray-700 dark:text-[var(--text-pri)] group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
              Allow tracks to override these settings
            </p>
            <p className="text-xs text-gray-500 dark:text-[var(--text-sec)] mt-0.5">
              When disabled, cycle defaults are locked — track admins cannot change them.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={allowTrackOverride}
            disabled={busy === 'allowTrackOverride'}
            onClick={() => handleAllowTrackOverrideChange(!allowTrackOverride)}
            className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 rounded-full border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 ${
              allowTrackOverride ? 'border-blue-600 bg-blue-600' : 'border-gray-300 bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                allowTrackOverride ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
        </label>
      </div>
    </div>
  );
}

export default CycleSessionDefaultsSection;
